import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * EIN WIDGET ROLLT NIE (Auftrag #38, SYS-12, NFA-07).
 *
 * Rückmeldung vom 30.09.2026: „In den Widgets sollte nicht gescrollt werden
 * müssen." Eine Karte auf dem Dashboard ist ein Blick, kein Fenster: Was
 * nicht hineinpasst, fällt nach Wichtigkeit weg, statt hinter einem
 * Rollbalken zu verschwinden.
 *
 * ============================================================================
 * WAS BLEIBT, WAS GEHT
 * ============================================================================
 *
 * Die direkten Kinder des Rumpfs tragen `data-teil` und `data-rang`:
 *
 *   rang 0  bleibt immer — die Zahl (der Titel steht im Kopf)
 *   rang 1  Zusatzzeilen: Veränderung, Ziel, Zusatz — beim Bericht das Diagramm
 *   rang 2  der Verlauf — beim Bericht Name und Stand
 *   rang 3  die Liste — sie bekommt, was übrig ist
 *
 * Gefüllt wird von oben: erst rang 1, dann rang 2, der Rest der Höhe gehört
 * der Liste. Wegfallen tut es also in umgekehrter Folge — zuerst wird die
 * Liste kürzer, dann geht der Verlauf, dann gehen die Zusatzzeilen. Eine
 * Liste (`[data-zeile]` darin) zeigt so viele Zeilen, wie GANZ passen —
 * ohne Hinweis auf den Rest (Auftrag #41: kein „+ N weitere"). Passt keine
 * Zeile, fällt die Liste weg. Eine Zeile `data-zeile="kopf"`
 * (Gruppenüberschrift) steht nie allein am Ende.
 *
 * Ein Teil mit `data-mindest` (das Diagramm des Berichts) wächst und zählt
 * mit dieser Mindesthöhe. Trägt er dazu `data-wunsch` (Auftrag CRM-145),
 * halten die Teile mit höherem Rang ihm so viel Höhe frei: Sie fallen weg,
 * bevor das Diagramm unter seinen Wunsch schrumpft. Ist das Feld zu niedrig
 * für den Wunsch, bleibt es bei der Mindesthöhe — das Diagramm geht nicht.
 *
 * GANZ ODER GAR NICHT (Auftrag CRM-145): Eine Liste mit `data-ganz` (die
 * Legende eines Bilds in Farbe) wird nie gekürzt — eine Farbe ohne Namen
 * führte in die Irre. Passt sie nicht ganz, fällt sie weg. Ein Teil mit
 * `data-statt="name"` ist die knappe Fassung eines anderen und steht nur,
 * wenn jener weggefallen ist.
 *
 * FLACH: Ist der Rumpf breiter als `flachAb`-mal seine Höhe (eine 2×1-Karte
 * hat rund 66 px Höhe, darunter passte kein Verlauf), steht der Teil mit
 * `data-nebenan` rechts neben den übrigen, in voller Höhe (Klasse
 * `widget__rumpf--flach`). Jede der beiden Spalten wird für sich gefüllt.
 *
 * ============================================================================
 * GEMESSEN, NICHT GESCHÄTZT
 * ============================================================================
 *
 * Wie viel passt, ergibt die tatsächliche Höhe — keine feste Zahl je Größe:
 * Unter 1024 px fließt das Raster, und eine 2×2-Karte ist auf dem Tablet
 * anders hoch als am Schreibtisch.
 *
 *   1. Ohne Plan zeichnet die Karte ALLES. Der Rumpf schneidet ab, er
 *      rollt nicht.
 *   2. `useLayoutEffect` misst vor dem Malen, legt den Plan fest, und React
 *      zeichnet ihn noch im selben Bild — man sieht nie die volle Fassung.
 *   3. Ändert sich die Größe (ResizeObserver) oder der Inhalt, beginnt es
 *      bei 1. Die Größe des Rumpfs hängt nicht am Inhalt (das Raster legt
 *      sie fest), deshalb schaukelt sich nichts auf.
 *
 * Ohne Layout (jsdom, Höhe 0) gilt „alles zeigen" — die Prüfungen der
 * Oberfläche sehen den vollen Inhalt; das Weglassen prüft der Browser
 * (`entwicklung/widgetprobe.mjs`) und `einpassen.test.tsx` mit gemessenen
 * Höhen.
 */

export type Einpassung = {
  /** Die Teile (`data-teil`), die wegfallen. */
  readonly weg: ReadonlySet<string>;
  /** Wie viele Zeilen der Liste stehen; null: alle. */
  readonly zeilen: number | null;
};

const ALLES: Einpassung = { weg: new Set(), zeilen: null };

function hoehe(el: Element): number {
  return el.getBoundingClientRect().height;
}

type Teil = {
  readonly el: HTMLElement;
  readonly name: string;
  readonly rang: number;
  readonly hoehe: number;
  /** Was über `hoehe` hinaus vor den nachrangigen Teilen freizuhalten ist (`data-wunsch`). */
  readonly vorbehalt: number;
};

/** Der Plan für einen gemessenen Rumpf — alles steht, der Rumpf schneidet ab. */
export function plane(rumpf: HTMLElement, nebenan: boolean): Einpassung {
  const verfuegbar = hoehe(rumpf);
  if (verfuegbar <= 0) return ALLES;
  const abstand = Number.parseFloat(getComputedStyle(rumpf).rowGap) || 0;

  const teile: Teil[] = [...rumpf.children]
    .filter((k): k is HTMLElement => k instanceof HTMLElement && k.dataset['teil'] !== undefined)
    .map((k) => ({
      el: k,
      name: k.dataset['teil']!,
      rang: Number(k.dataset['rang'] ?? '0'),
      hoehe: k.dataset['mindest'] !== undefined ? Number(k.dataset['mindest']) : hoehe(k),
      vorbehalt: k.dataset['mindest'] !== undefined && k.dataset['wunsch'] !== undefined
        ? Math.max(0, Number(k.dataset['wunsch']) - Number(k.dataset['mindest']))
        : 0,
    }));

  /* Flach: die Spalte daneben hat die ganze Höhe für sich. */
  const seite = nebenan ? teile.filter((t) => t.el.dataset['nebenan'] !== undefined) : [];
  const haupt = teile.filter((t) => !seite.includes(t));
  const a = fuelle(haupt, verfuegbar, abstand);
  const b = fuelle(seite, verfuegbar, abstand);
  return { weg: new Set([...a.weg, ...b.weg]), zeilen: a.zeilen ?? b.zeilen };
}

/**
 * Eine Spalte füllen: Rang 0 immer, dann nach Rang; die Liste bekommt den
 * Rest. Passt keine ihrer Zeilen, fällt sie ganz weg.
 */
function fuelle(teile: readonly Teil[], verfuegbar: number, abstand: number): { weg: Set<string>; zeilen: number | null } {
  const weg = new Set<string>();
  let belegt = 0;
  let anzahl = 0;
  /* Auftrag CRM-145: die Höhe, die ein schon gesetztes Diagramm über seiner Mindesthöhe behält. */
  let vorbehalt = 0;
  const passt = (h: number): boolean => belegt + (anzahl > 0 ? abstand : 0) + h + vorbehalt <= verfuegbar + 0.5;
  const nimm = (h: number): void => {
    belegt += (anzahl > 0 ? abstand : 0) + h;
    anzahl += 1;
  };

  // Rang 0 bleibt, auch wenn er allein nicht passt — der Rumpf schneidet dann ab.
  for (const t of teile.filter((t) => t.rang === 0)) nimm(t.hoehe);

  let zeilen: number | null = null;
  for (const t of teile.filter((t) => t.rang > 0).sort((x, y) => x.rang - y.rang)) {
    const statt = t.el.dataset['statt'];
    if (statt !== undefined && !weg.has(statt)) {
      weg.add(t.name);
      continue;
    }
    /* Nur die eigenen Zeilen — nicht die einer Liste, die sich selbst einpasst (Legende). */
    const reihe = [...t.el.querySelectorAll<HTMLElement>('[data-zeile]')].filter((z) => z.closest('[data-teil]') === t.el);
    if (reihe.length === 0 || t.el.dataset['ganz'] !== undefined) {
      if (passt(t.hoehe)) {
        nimm(t.hoehe);
        /* Was schon steht, behält seinen Platz; der Vorbehalt gilt erst den Teilen danach. */
        vorbehalt += t.vorbehalt;
      } else weg.add(t.name);
      continue;
    }
    /* Eine Liste: so viele Zeilen, wie ganz passen (Auftrag #41: ohne „+ N weitere"). */
    const zeilenH = reihe.map(hoehe);
    const fest = t.hoehe - zeilenH.reduce((x, y) => x + y, 0);
    let n = reihe.length;
    const braucht = (k: number): number => fest + zeilenH.slice(0, k).reduce((x, y) => x + y, 0);
    while (n > 0 && !passt(braucht(n))) n -= 1;
    while (n > 0 && reihe[n - 1]!.dataset['zeile'] === 'kopf') n -= 1;
    if (echte(reihe, n) === 0) {
      weg.add(t.name);
      continue;
    }
    nimm(braucht(n));
    zeilen = n === reihe.length ? null : n;
  }
  return { weg, zeilen };
}

/** Wie viele echte Einträge (keine Gruppenköpfe) unter den ersten `n` Zeilen stehen. */
function echte(reihe: readonly HTMLElement[], n: number): number {
  return reihe.slice(0, n).filter((z) => z.dataset['zeile'] !== 'kopf').length;
}

/**
 * Misst den Rumpf und liefert den Plan. `inhalt` sind die Daten, die die
 * Karte zeichnet — ändern sie sich, wird neu gemessen. Verglichen wird mit
 * `Object.is`: nur Zustand, Eigenschaften oder Texte übergeben, nie ein Feld,
 * das beim Zeichnen neu entsteht — sonst misst die Karte ohne Ende.
 */
export function useEinpassen<E extends HTMLElement = HTMLDivElement>(
  inhalt: readonly unknown[],
  optionen: { readonly flachAb?: number } = {},
): {
  readonly ref: RefObject<E | null>;
  readonly plan: Einpassung | null;
  readonly flach: boolean;
  /** Die gemessene Breite des Rumpfs; null vor der ersten Messung. */
  readonly breite: number | null;
} {
  const ref = useRef<E | null>(null);
  const [plan, setzePlan] = useState<Einpassung | null>(null);
  const [groesse, setzeGroesse] = useState<{ breite: number; hoehe: number } | null>(null);
  const nebenan =
    optionen.flachAb !== undefined && groesse !== null && groesse.hoehe > 0 && groesse.breite > optionen.flachAb * groesse.hoehe;

  /* Die Größe gleich beim ersten Zeichnen — nicht erst, wenn der Beobachter meldet. */
  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (r !== undefined) setzeGroesse({ breite: Math.round(r.width), hoehe: Math.round(r.height) });
  }, []);

  /* Neuer Inhalt oder neue Größe: noch einmal alles zeichnen und messen. */
  useLayoutEffect(() => {
    setzePlan(null);
    /* Die Daten der Karte sind die Abhängigkeit — als Liste übergeben. */
  }, [...inhalt, groesse?.breite, groesse?.hoehe, nebenan]);

  useLayoutEffect(() => {
    if (plan !== null || ref.current === null) return;
    setzePlan(plane(ref.current, nebenan));
  }, [plan, nebenan]);

  useEffect(() => {
    const el = ref.current;
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const beobachter = new ResizeObserver(([e]) => {
      if (e === undefined) return;
      const neu = { breite: Math.round(e.contentRect.width), hoehe: Math.round(e.contentRect.height) };
      setzeGroesse((alt) => (alt !== null && alt.breite === neu.breite && alt.hoehe === neu.hoehe ? alt : neu));
    });
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);

  return { ref, plan, flach: nebenan, breite: groesse?.breite ?? null };
}

/** Ob ein Teil gezeichnet wird — beim Messen (ohne Plan) immer. */
export function zeigt(plan: Einpassung | null, teil: string): boolean {
  return plan === null || !plan.weg.has(teil);
}
